/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled from "styled-components";
import Text from "./ui/Text";
import InvoiceStatusBadge from "./InvoiceStatusBadge";
import FormatPrice from "./formatters/FormatPrice";
import SummaryTotals from "./booking/checkout/SummaryTotals";
import SummaryItemsTable from "./booking/checkout/SummaryTable";
import { invoiceLinesToItems } from "../utils/invoiceItems";
import { DISCOUNT_TYPE, INVOICE_STATUS } from "../constants/constants";
import FormatDateTime from "./formatters/FormatDateTime";
import Stack from "./ui/Stack";
import { RecordCard, RecordCardHeader, RecordCardHeaderCol, RecordCardHeaderColItem, TimezoneNote } from "./ui/RecordCard";

const InvoiceCardFooter = styled.div`
  padding: 0.5rem 1rem 0 0;
`;

const InvoiceCardFooterTotals = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
`;

const InvoiceCardFooterTotalsItem = styled.div`
  display: flex;
  align-items: center;

  & > *:first-child::after {
    content: ":";
    margin-right: 0.25rem;
  }
`;

const InvoiceCard = ({ invoice, renderLineExtra }) => {
  return (
    <Stack $gap="0.5rem">
      <RecordCard>
        <RecordCardHeader>
          <RecordCardHeaderCol>
            <RecordCardHeaderColItem>
              <Text color="dark" size="small">
                invoice_payment_processor
              </Text>
              <Text color="dark" size="small" weight="bold">
                {invoice.payment_processor}
              </Text>
            </RecordCardHeaderColItem>
            {invoice.promotion_instances?.length > 0 && (
              <RecordCardHeaderColItem>
                <Text color="dark" size="small">
                  Promocode
                </Text>
                <Text color="dark" size="small" weight="bold">
                  {invoice.promotion_instances.map((instance, index) => {
                    const promotion = instance.promotion || {};
                    const hasDiscount = promotion.discount != null;
                    const discountValue =
                      promotion.discount_type === DISCOUNT_TYPE.PERCENTAGE ? (
                        `${promotion.discount}%`
                      ) : (
                        <FormatPrice value={promotion.discount} currency={promotion.currency} />
                      );

                    return (
                      <React.Fragment key={instance.id}>
                        {index > 0 && ", "}
                        {instance.code}
                        {hasDiscount && <> | {discountValue}</>}
                      </React.Fragment>
                    );
                  })}
                </Text>
              </RecordCardHeaderColItem>
            )}
            <RecordCardHeaderColItem>
              <Text color="dark" size="small">
                invoice_status
              </Text>
              <InvoiceStatusBadge status={invoice.status} payment_received={invoice.payment_received} />
            </RecordCardHeaderColItem>
          </RecordCardHeaderCol>
          <RecordCardHeaderCol $align="right">
            <RecordCardHeaderColItem>
              <Text color="dark" size="small">
                invoice_date
              </Text>
              <Text color="dark" size="small" weight="bold">
                <FormatDateTime value={invoice.datetime} format={FormatDateTime.FORMAT.DATETIME} />
              </Text>
            </RecordCardHeaderColItem>
            <RecordCardHeaderColItem>
              <Text color="dark" size="small">
                invoice_due_date
              </Text>
              <Text color="dark" size="small" weight="bold">
                <FormatDateTime value={invoice.due_datetime} format={FormatDateTime.FORMAT.DATETIME} />
              </Text>
            </RecordCardHeaderColItem>
          </RecordCardHeaderCol>
        </RecordCardHeader>
        <SummaryItemsTable
          items={invoiceLinesToItems(invoice)}
          invoice={invoice}
          renderLineExtra={renderLineExtra}
        />
        {/* TODO: Possibly rename component of SummaryTotals to InvoiceTotals */}
        {(invoice.status === INVOICE_STATUS.PAID && !invoice.payment_received) && <SummaryTotals invoice={invoice} />}

        {(invoice.status === INVOICE_STATUS.PAID && invoice.payment_received) &&
          (<InvoiceCardFooter>
            <InvoiceCardFooterTotals>
              <InvoiceCardFooterTotalsItem>
                <Text color="dark">
                  invoice_line_tax
                </Text>
                <Text color="dark" weight="bold">
                  <FormatPrice value={invoice.tax_amount} currency={invoice.currency} />
                </Text>
              </InvoiceCardFooterTotalsItem>
              {invoice.discount_amount > 0 && (
                <InvoiceCardFooterTotalsItem>
                  <Text color="dark">
                    invoice_line_discount
                  </Text>
                  <Text color="dark" weight="bold">
                    <FormatPrice value={invoice.discount_amount} currency={invoice.currency} />
                  </Text>
                </InvoiceCardFooterTotalsItem>
              )}
              <InvoiceCardFooterTotalsItem>
                <Text color="dark">
                  Total
                </Text>
                <Text color="dark" weight="bold">
                  <FormatPrice value={invoice.amount} currency={invoice.currency} />
                </Text>
              </InvoiceCardFooterTotalsItem>
            </InvoiceCardFooterTotals>
          </InvoiceCardFooter>
          )}
      </RecordCard>
      <TimezoneNote />
    </Stack>
  );
};

export default InvoiceCard;
